import React, { useState } from 'react';
import { X, Eye, CheckCircle, ArrowRight, Calendar, Clock } from 'lucide-react';
import { format, addDays, startOfWeek } from 'date-fns';

const SmartActionPreview = ({ insight, timeline, onApply, onDismiss, onClose }) => {
  const [isApplying, setIsApplying] = useState(false);

  const handleApply = async () => {
    setIsApplying(true);
    try {
      await onApply(insight);
      onClose();
    } catch (error) {
      console.error('Error applying suggestion:', error);
    } finally {
      setIsApplying(false);
    }
  };

  // Get context-specific button text based on insight action type
  const getActionButtonText = () => {
    if (isApplying) return 'Applying...';

    switch (insight.action?.type) {
      case 'preview':
        return 'Redistribute Tasks';
      case 'suggest_tasks':
        return 'Move to This Week';
      case 'preview_batch':
        return 'Group These Tasks';
      case 'focus_prerequisite':
        return 'Focus on Prerequisites';
      case 'show_next_tasks':
        return 'View Next Tasks';
      case 'filter_category':
        return 'Apply Filter';
      case 'suggest_prep_session':
        return 'Schedule Prep Session';
      default:
        return 'Apply Changes';
    }
  };

  const handleDismiss = () => {
    onDismiss(insight.id);
    onClose();
  };

  const renderPreviewContent = () => {
    switch (insight.action?.type) {
      case 'preview':
        return <WorkloadPreview insight={insight} timeline={timeline} />;
      case 'suggest_tasks':
        return <TaskSuggestionPreview insight={insight} timeline={timeline} />;
      case 'preview_batch':
        return <BatchingPreview insight={insight} timeline={timeline} />;
      case 'focus_prerequisite':
        return <DependencyPreview insight={insight} timeline={timeline} />;
      default:
        return <DefaultPreview insight={insight} />;
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="p-6 border-b border-stone-200">
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-3">
              <div className={`p-2 rounded-lg ${insight.style.bgColor}`}>
                <span className="text-lg">{insight.icon}</span>
              </div>
              <div>
                <h2 className="text-xl font-semibold text-slate-900 mb-1">
                  {insight.title}
                </h2>
                <p className="text-sm text-slate-600 mb-2">
                  <strong>What I noticed:</strong> {insight.insight}
                </p>
                <p className="text-sm text-azure-600">
                  <strong>Opportunity:</strong> {insight.opportunity}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-stone-100 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-stone-500" />
            </button>
          </div>
        </div>

        {/* Preview Content */}
        <div className="p-6 overflow-y-auto max-h-[60vh]">
          {renderPreviewContent()}
        </div>

        {/* Actions */}
        <div className="p-6 border-t border-stone-200 bg-stone-50">
          <div className="flex items-center justify-between">
            <div className="text-xs text-stone-500">
              <Eye className="w-4 h-4 inline mr-1" />
              Preview mode - see what would change before applying
            </div>
            <div className="flex gap-3">
              <button
                onClick={handleDismiss}
                className="px-4 py-2 text-sm text-slate-600 hover:text-slate-800 transition-colors"
              >
                Don't show this again
              </button>
              <button
                onClick={onClose}
                className="px-4 py-2 text-sm border border-stone-300 text-slate-700 rounded-lg hover:bg-stone-50 transition-colors"
              >
                Maybe later
              </button>
              {insight.action && (
                <button
                  onClick={handleApply}
                  disabled={isApplying}
                  className="px-6 py-2 text-sm bg-azure-600 text-white rounded-lg hover:bg-azure-700 disabled:opacity-50 transition-colors flex items-center gap-2"
                  title={`Apply this ${insight.type} suggestion`}
                >
                  {isApplying ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Applying...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-4 h-4" />
                      {getActionButtonText()}
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Preview component for workload redistribution
const WorkloadPreview = ({ insight, timeline }) => {
  const targetWeek = insight.action.data.targetWeek;
  const weekTasks = timeline.filter(task => {
    if (!task.due_date) return false;
    const taskDate = new Date(task.due_date);
    return taskDate >= targetWeek && taskDate <= addDays(targetWeek, 6);
  });

  const totalHours = weekTasks.reduce((sum, t) => sum + (t.estimated_hours || 0), 0);
  const recommendedMoves = weekTasks
    .filter(t => t.priority !== 'high')
    .slice(0, 3);

  return (
    <div className="space-y-4">
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
        <h3 className="font-medium text-amber-900 mb-2">Current Situation</h3>
        <div className="text-sm text-amber-800">
          <p>Week of {format(targetWeek, 'MMM d')} currently has:</p>
          <ul className="mt-2 space-y-1">
            <li>• {weekTasks.length} tasks scheduled</li>
            <li>• {totalHours} total hours</li>
            <li>• {weekTasks.filter(t => t.priority === 'high').length} high-priority deadlines</li>
          </ul>
        </div>
      </div>

      <div className="bg-azure-50 border border-azure-200 rounded-lg p-4">
        <h3 className="font-medium text-azure-900 mb-2">Suggested Changes</h3>
        <div className="text-sm text-azure-800">
          <p>Move these flexible tasks to lighter weeks:</p>
          <div className="mt-3 space-y-2">
            {recommendedMoves.map(task => (
              <div key={task.id} className="flex items-center justify-between bg-white p-2 rounded border">
                <span>{task.title}</span>
                <div className="flex items-center gap-2 text-xs text-stone-500">
                  <Clock className="w-3 h-3" />
                  {task.estimated_hours || 0.5}h
                  <ArrowRight className="w-3 h-3" />
                  <span>Week of {format(addDays(targetWeek, 7), 'MMM d')}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
        <h3 className="font-medium text-emerald-900 mb-2">Expected Outcome</h3>
        <div className="text-sm text-emerald-800">
          <p>After redistribution:</p>
          <ul className="mt-2 space-y-1">
            <li>• Week reduces from {totalHours}h to ~{totalHours - recommendedMoves.reduce((sum, t) => sum + (t.estimated_hours || 0), 0)}h</li>
            <li>• Better focus on high-priority items</li>
            <li>• More sustainable workload balance</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

// Preview component for task suggestions
const TaskSuggestionPreview = ({ insight, timeline }) => {
  const data = insight.action?.data || {};
  const { targetWeek, availableHours } = data;
  const suggestedTasks = insight.tasks || data.suggestedTasks || [];

  // If no specific suggestions, find suitable tasks
  const candidateTasks = suggestedTasks.length > 0 ? suggestedTasks : timeline
    .filter(task =>
      task.status !== 'completed' &&
      task.category !== 'financial' && // Exclude FAFSA/financial tasks from deep work
      !task.title?.toLowerCase().includes('fafsa') &&
      !task.title?.toLowerCase().includes('css') &&
      (!task.due_date || new Date(task.due_date) > addDays(targetWeek, 7))
    )
    .slice(0, 5);

  return (
    <div className="space-y-4">
      <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
        <h3 className="font-medium text-emerald-900 mb-2">Strategic Window</h3>
        <div className="text-sm text-emerald-800">
          <p>Week of {format(targetWeek, 'MMM d')} has {Math.round(availableHours || 0)} available hours</p>
          <p className="mt-1 text-xs">Perfect for focused deep work or getting ahead</p>
        </div>
      </div>

      <div className="bg-azure-50 border border-azure-200 rounded-lg p-4">
        <h3 className="font-medium text-azure-900 mb-2">Suggested Focus Areas</h3>
        <div className="space-y-2">
          {candidateTasks.length > 0 ? candidateTasks.map((task, index) => (
            <div key={task.id || index} className="flex items-center justify-between bg-white p-2 rounded border">
              <div>
                <span className="text-sm font-medium">{task.title}</span>
                <div className="text-xs text-stone-500">{task.category}</div>
              </div>
              <div className="flex items-center gap-2 text-xs text-stone-500">
                <Clock className="w-3 h-3" />
                {task.estimated_hours || 0.5}h
              </div>
            </div>
          )) : (
            <div className="text-center py-4 text-sm text-stone-500">
              No specific tasks available for this week yet.
            </div>
          )}
        </div>
      </div>

      <div className="bg-violet-50 border border-violet-200 rounded-lg p-4">
        <h3 className="font-medium text-violet-900 mb-2">Why This Works</h3>
        <div className="text-sm text-violet-800">
          <ul className="space-y-1">
            <li>• Getting ahead reduces future pressure</li>
            <li>• Light weeks enable higher quality work</li>
            <li>• Creates buffer time for unexpected priorities</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

// Preview component for batching opportunities
const BatchingPreview = ({ insight, timeline }) => {
  const batchData = insight.action.data;
  const tasks = insight.tasks || batchData?.tasks || [];

  if (!batchData || tasks.length === 0) {
    return (
      <div className="text-center py-8">
        <p className="text-sm text-stone-500">No tasks available for batching at this time.</p>
        <p className="text-xs text-stone-400 mt-2">Tasks may be too far apart or not yet available.</p>
      </div>
    );
  }

  const totalHours = tasks.reduce((sum, t) => sum + (t.estimated_hours || 0), 0);

  return (
    <div className="space-y-4">
      <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
        <h3 className="font-medium text-indigo-900 mb-2">Batching Opportunity</h3>
        <div className="text-sm text-indigo-800">
          <p>{tasks.length} {batchData.category || 'similar'} tasks ready for focused session</p>
          <p className="mt-1">Total time: ~{totalHours.toFixed(1)}h</p>
        </div>
      </div>

      <div className="space-y-2">
        <h4 className="font-medium text-slate-900">Tasks to batch:</h4>
        {tasks.slice(0, 6).map((task, index) => (
          <div key={task.id || index} className="flex items-center justify-between bg-stone-50 p-3 rounded border">
            <span className="text-sm">{task.title}</span>
            <div className="flex items-center gap-2 text-xs text-stone-500">
              <Clock className="w-3 h-3" />
              {task.estimated_hours || 0.5}h
            </div>
          </div>
        ))}
        {tasks.length > 6 && (
          <p className="text-sm text-stone-500 italic">...and {tasks.length - 6} more</p>
        )}
      </div>

      <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
        <h3 className="font-medium text-emerald-900 mb-2">Batching Benefits</h3>
        <div className="text-sm text-emerald-800">
          <ul className="space-y-1">
            <li>• Maintain focus and context</li>
            <li>• Higher quality, consistent output</li>
            <li>• Sense of completion and momentum</li>
            <li>• Efficient use of mental energy</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

// Preview component for dependency insights
const DependencyPreview = ({ insight, timeline }) => {
  const chain = insight.action?.data;

  if (!chain || !chain.hasActualTasks) {
    return (
      <div className="text-center py-8">
        <p className="text-sm text-stone-500">Task dependencies not available at this time.</p>
        <p className="text-xs text-stone-400 mt-2">No tasks found matching this pattern.</p>
      </div>
    );
  }

  const prereqTasks = timeline.filter(t =>
    t.category === chain.prerequisite && t.status !== 'completed'
  );

  const dependentTasks = timeline.filter(t =>
    t.category === chain.dependent && t.status !== 'completed'
  );

  return (
    <div className="space-y-4">
      <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
        <h3 className="font-medium text-orange-900 mb-2">Task Sequencing</h3>
        <div className="text-sm text-orange-800">
          <p>Completing {chain.prerequisite} tasks enables smoother progress on {chain.dependentCount} related items</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <h4 className="font-medium text-slate-900 mb-2">Complete First:</h4>
          <div className="space-y-2">
            {prereqTasks.slice(0, 3).map(task => (
              <div key={task.id} className="bg-orange-50 p-2 rounded border border-orange-200 text-sm">
                {task.title}
              </div>
            ))}
          </div>
        </div>

        <div>
          <h4 className="font-medium text-slate-900 mb-2">Then Enables:</h4>
          <div className="space-y-2">
            {dependentTasks.slice(0, 3).map(task => (
              <div key={task.id} className="bg-emerald-50 p-2 rounded border border-emerald-200 text-sm">
                {task.title}
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="bg-azure-50 border border-azure-200 rounded-lg p-4">
        <h3 className="font-medium text-azure-900 mb-2">Strategic Impact</h3>
        <div className="text-sm text-azure-800">
          <p>Focusing on prerequisites first creates momentum and enables smoother progress</p>
        </div>
      </div>
    </div>
  );
};

// Default preview for insights without specific preview logic
const DefaultPreview = ({ insight }) => {
  return (
    <div className="space-y-4">
      <div className={`${insight.style.bgColor} border ${insight.style.borderColor} rounded-lg p-4`}>
        <h3 className={`font-medium ${insight.style.textColor} mb-2`}>Strategic Insight</h3>
        <div className={`text-sm ${insight.style.textColor}`}>
          <p><strong>What I noticed:</strong> {insight.insight}</p>
          <p className="mt-2"><strong>Opportunity:</strong> {insight.opportunity}</p>
        </div>
      </div>

      <div className="bg-stone-50 border border-stone-200 rounded-lg p-4">
        <h3 className="font-medium text-slate-900 mb-2">About This Suggestion</h3>
        <div className="text-sm text-slate-700">
          <p>This insight is based on analysis of your current timeline and productivity patterns.</p>
          <p className="mt-2">These suggestions help you stay strategic about your college journey.</p>
        </div>
      </div>
    </div>
  );
};

export default SmartActionPreview;